/**
 * AgentTrace — визуализация последовательности шагов агента (ReAct trace).
 *
 * Показывает:
 *   - agent_step (нумерация)
 *   - agent_thought (что агент думает)
 *   - tool_call → tool_result (с display_summary, expandable args)
 *   - agent_warning (timeout/budget)
 *
 * Финальный answer выводится отдельно в AgentPage, не в этом trace.
 */
import { useState } from "react";

export interface AgentEvent {
  type: string;
  // possible fields based on event type
  step?: number;
  max_steps?: number;
  content?: string;
  tool?: string;
  args?: Record<string, unknown>;
  is_error?: boolean;
  display_summary?: string;
  metadata?: Record<string, unknown>;
  elapsed_ms?: number;
  id?: string;
  reason?: string;
  error?: string;
  session_id?: string;
  budget?: { max_steps: number; max_seconds: number };
  final_reason?: string;
  steps_used?: number;
  tokens_used?: number;
  final_answer?: string;
  [k: string]: unknown;
}

interface Props {
  events: AgentEvent[];
  isRunning: boolean;
}

interface Step {
  number: number;
  thoughts: string[];
  toolCalls: Array<{
    id: string;
    tool: string;
    args: Record<string, unknown>;
    result?: { is_error: boolean; display_summary: string; metadata?: Record<string, unknown>; elapsed_ms?: number };
  }>;
  criticVerdict?: {
    passed: boolean;
    faithful: boolean;
    complete: boolean;
    feedback?: string;
  };
}

function groupEventsByStep(events: AgentEvent[]): Step[] {
  const steps: Step[] = [];
  let current: Step | null = null;

  for (const e of events) {
    if (e.type === "agent_step") {
      const n = typeof e.step === "number" ? e.step : 0;
      if (n > 0) {
        current = { number: n, thoughts: [], toolCalls: [] };
        steps.push(current);
      }
    } else if (e.type === "agent_thought" && current && typeof e.content === "string") {
      current.thoughts.push(e.content);
    } else if (e.type === "tool_call" && current) {
      current.toolCalls.push({
        id: String(e.id || ""),
        tool: String(e.tool || "unknown"),
        args: (e.args as Record<string, unknown>) || {},
      });
    } else if (e.type === "tool_result" && current) {
      const matchId = String(e.id || "");
      const tc = current.toolCalls.find((x) => x.id === matchId) ||
                 current.toolCalls[current.toolCalls.length - 1];
      if (tc) {
        tc.result = {
          is_error: !!e.is_error,
          display_summary: String(e.display_summary || ""),
          metadata: (e.metadata as Record<string, unknown>) || {},
          elapsed_ms: typeof e.elapsed_ms === "number" ? e.elapsed_ms : undefined,
        };
      }
    } else if (e.type === "critic_verdict" && current) {
      current.criticVerdict = {
        passed: !!e.passed,
        faithful: !!e.faithful,
        complete: !!e.complete,
        feedback: typeof e.feedback === "string" ? e.feedback : undefined,
      };
    }
  }
  return steps;
}

function ToolCallItem({ call }: { call: Step["toolCalls"][number] }) {
  const [expanded, setExpanded] = useState(false);
  const r = call.result;
  return (
    <div className="my-2 border-l-2 border-gray-300 dark:border-gray-600 pl-3">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="text-left text-sm w-full hover:text-blue-600 dark:hover:text-blue-400"
      >
        <span className="font-mono text-xs text-purple-700 dark:text-purple-400">
          {call.tool}
        </span>
        {r ? (
          <span className={r.is_error ? "ml-2 text-red-600 dark:text-red-400" : "ml-2 text-gray-700 dark:text-gray-300"}>
            {r.display_summary}
            {r.elapsed_ms != null && <span className="ml-1 text-xs text-gray-500">({(r.elapsed_ms / 1000).toFixed(1)}s)</span>}
          </span>
        ) : (
          <span className="ml-2 text-gray-500 italic">running…</span>
        )}
      </button>
      {expanded && (
        <div className="mt-1 ml-2 text-xs space-y-1">
          <div className="text-gray-600 dark:text-gray-400">
            <strong>args:</strong>{" "}
            <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">
              {JSON.stringify(call.args)}
            </code>
          </div>
          {r?.metadata && Object.keys(r.metadata).length > 0 && (
            <div className="text-gray-600 dark:text-gray-400">
              <strong>meta:</strong>{" "}
              <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">
                {JSON.stringify(r.metadata)}
              </code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AgentTrace({ events, isRunning }: Props) {
  const steps = groupEventsByStep(events);
  if (steps.length === 0 && !isRunning) return null;

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Шаги агента {isRunning && <span className="text-blue-600 animate-pulse">(работаю…)</span>}
      </div>
      <div className="space-y-3">
        {steps.map((step) => (
          <div key={step.number} className="border border-gray-200 dark:border-gray-700 rounded p-3 bg-gray-50 dark:bg-gray-800/50">
            <div className="text-xs text-gray-500 mb-1">Шаг {step.number}</div>
            {step.thoughts.length > 0 && (
              <div className="text-sm italic text-gray-700 dark:text-gray-300 mb-2">
                💭 {step.thoughts.join(" ")}
              </div>
            )}
            {step.toolCalls.map((c) => (
              <ToolCallItem key={c.id || c.tool} call={c} />
            ))}
            {step.criticVerdict && (
              <div
                className={
                  "mt-2 text-xs rounded p-2 " +
                  (step.criticVerdict.passed
                    ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                    : "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300")
                }
              >
                <span className="font-medium">
                  {step.criticVerdict.passed ? "✓ Critic OK" : "⚠ Critic flagged"}
                </span>
                <span className="ml-2 text-[11px]">
                  faithful={String(step.criticVerdict.faithful)}, complete={String(step.criticVerdict.complete)}
                </span>
                {step.criticVerdict.feedback && (
                  <div className="mt-1 italic">{step.criticVerdict.feedback}</div>
                )}
              </div>
            )}
            {step.thoughts.length === 0 && step.toolCalls.length === 0 && isRunning && (
              <div className="text-xs text-gray-400 italic">…</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
